import {
  APP_INITIALIZER,
  ApplicationRef,
  DoBootstrap,
  ErrorHandler,
  Injector,
  LOCALE_ID,
  NgModule
} from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'

import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { createCustomElement, NgElementConstructor } from '@angular/elements'
import 'zone.js'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { MsalModule, MsalRedirectComponent } from '@azure/msal-angular'
import { InteractionType, PublicClientApplication } from '@azure/msal-browser'
import { environment } from '../environments/environment'
import * as Sentry from '@sentry/angular-ivy'
import { Router } from '@angular/router'
import { JwtAuthInterceptor } from './interceptors/jwt-auth.interceptor'
import { registerLocaleData } from '@angular/common'
import localeEn from '@angular/common/locales/en'
import localeEs from '@angular/common/locales/es'
import localeSv from '@angular/common/locales/sv'
import localeNo from '@angular/common/locales/no'
import localeCs from '@angular/common/locales/cs'
import { TopBarComponent } from '@components/top-bar/top-bar.component'
import { SidebarComponent } from '@components/sidebar/sidebar.component'
import { FooterComponent } from '@components/footer/footer.component'
import { LoadingInterceptor } from './interceptors/loading.interceptor'
import { LoadingComponent } from '@components/loading/loading.component'
import { CAcademyComponent } from './pages/c-academy/c-academy.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core'
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FabricatorSelectInvoicesComponent } from './pages/register-sales-form/fabricator-select-invoices/fabricator-select-invoices.component'
import { LogoutComponent } from './pages/logout/logout.component'

registerLocaleData(localeEn, 'en-US');
registerLocaleData(localeEn, 'en-GB');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeSv, 'sv');
registerLocaleData(localeNo, 'no');
registerLocaleData(localeCs, 'cs');

const timestamp = Date.now().toString()

export function HttpLoaderFactory(http: HttpClient) {
    const isWebComponent = environment.webComponent
    if (isWebComponent) {
        return new TranslateHttpLoader(http, environment.publicUrl + '/assets/i18n/', '.json');
    } else {
        return new TranslateHttpLoader(http, './assets/i18n/', '.json')
    }
}

const isIE =
    window.navigator.userAgent.indexOf("MSIE ") > -1 ||
    window.navigator.userAgent.indexOf("Trident/") > -1;

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

export const WE_DEFAULT_LOCALE: string = 'en-US';
export const WE_LOCALE: string = ((new URL(window.location.href)).searchParams.get('language') || WE_DEFAULT_LOCALE).replace('_', '-');
export const WE_LOCALES: string[] = ['cs', 'da', 'de', 'en', 'en-au', 'en-ca', 'en-gb', 'es', 'fi', 'fr', 'fr-ca', 'hr', 'hu', 'it', 'iw', 'ja', 'nl', 'no', 'pl', 'pt', 'sk', 'sl', 'sv', 'tr'];

console.log('is safari: ', isSafari)

@NgModule({
    declarations: [AppComponent, LogoutComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        LoadingComponent,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        MsalModule.forRoot(
            new PublicClientApplication({
                auth: {
                    clientId: environment.msal.clientId, // Application (client) ID from the app registration
                    authority: environment.msal.authority, // The Azure cloud instance and the app's sign-in audience (tenant ID, common, organizations, or consumers)
                    knownAuthorities: environment.msal.knownAuthorities,
                    redirectUri: environment.msal.redirectUri, // This is your redirect URI
                    navigateToLoginRequestUrl: true,
                    postLogoutRedirectUri: environment.msal.redirectUri,
                },
            }),
            {
                // MSAL Guard Configuration
                interactionType: InteractionType.Redirect,
            },
            {
                interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
                protectedResourceMap: new Map([['https://graph.microsoft.com/v1.0/me', ['user.read']]]),
            },
        ),
        TopBarComponent,
        SidebarComponent,
        FooterComponent,
        CAcademyComponent,
        MatDatepickerModule,
        MatNativeDateModule,
    ],
    providers: [
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        { provide: HTTP_INTERCEPTORS, useClass: JwtAuthInterceptor, multi: true },
        { provide: LOCALE_ID, useValue: WE_LOCALE },
        { provide: MAT_DATE_LOCALE, useValue: WE_LOCALE },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingInterceptor,
            multi: true,
        },
        provideAnimationsAsync(),
        MatDatepickerModule,
    ],
})
export class AppModule implements DoBootstrap {
    constructor(private injector: Injector) {
        const we: NgElementConstructor<AppComponent> = createCustomElement(AppComponent, { injector: this.injector })
        customElements.define('cos-we', we)
    }

    ngDoBootstrap(appRef: ApplicationRef): void {
        if (!environment.webComponent) {
            appRef.bootstrap(AppComponent)
            appRef.bootstrap(MsalRedirectComponent)
        }
    }
}
